/**
 * OnboardingDrawer Component
 * Quick onboarding modal for items from OrdersPage
 */

import { useState, useCallback, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Save, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ImageUpload, type UploadedImage } from '@/components/common/ImageUpload';
import { itemService } from '@/services/itemService';
import type { CreateItemRequest } from '@/types/item';
import { isDemoMode, formatPrice } from '@/lib/demoPrice';

// Available categories for items
const ITEM_CATEGORIES = [
  'Entrantes',
  'Principales',
  'Postres',
  'Bebidas',
  'Vinos',
  'Cócteles',
  'Cafés',
  'Otros',
] as const;

// Validation schema - only require photo for onboarding
const onboardingSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido').max(255),
  description: z.string().optional(),
  category: z.string().optional(),
  price_cents: z.coerce.number().int().min(0).optional().nullable(),
  currency: z.string().default('EUR'),
  remote_item_id: z.string().optional(),
});

type OnboardingFormValues = z.infer<typeof onboardingSchema>;

export interface OnboardingItemData {
  remote_item_id?: string;
  name: string;
  price_cents?: number;
  currency?: string;
}

interface OnboardingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemData: OnboardingItemData | null;
}

export function OnboardingDrawer({
  open,
  onOpenChange,
  itemData,
}: OnboardingDrawerProps) {
  const queryClient = useQueryClient();
  const [images, setImages] = useState<UploadedImage[]>([]);

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      price_cents: null,
      currency: 'EUR',
      remote_item_id: '',
    },
  });

  // Pre-populate form when itemData changes
  useEffect(() => {
    if (itemData && open) {
      form.reset({
        name: itemData.name,
        description: '',
        category: '',
        price_cents: itemData.price_cents || null,
        currency: itemData.currency || 'EUR',
        remote_item_id: itemData.remote_item_id || '',
      });
      setImages([]);
    }
  }, [itemData, open, form]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateItemRequest) => itemService.createItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      queryClient.invalidateQueries({ queryKey: ['admin-orders'] });
      toast.success('Item onboarded correctamente');
      onOpenChange(false);
      form.reset();
      setImages([]);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { error?: string } } };
      toast.error(apiError?.response?.data?.error || 'Error al onboardear el item');
    },
  });

  /**
   * Handle image changes
   */
  const handleImagesChange = useCallback(
    (
      newImagesOrUpdater: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])
    ) => {
      setImages((prevImages) => {
        return typeof newImagesOrUpdater === 'function'
          ? newImagesOrUpdater(prevImages)
          : newImagesOrUpdater;
      });
    },
    []
  );

  /**
   * Handle form submission
   */
  const onSubmit = async (values: OnboardingFormValues) => {
    // Validate at least one photo
    if (images.length === 0) {
      toast.error('Debes subir al menos una foto del plato');
      return;
    }

    // Clean values
    const cleanedValues = {
      ...values,
      description: values.description || undefined,
      category: values.category || undefined,
      remote_item_id: values.remote_item_id || undefined,
    };

    // Extract signed IDs from images
    const photoSignedIds = images
      .filter((img) => img.signedId)
      .map((img) => img.signedId!);

    if (photoSignedIds.length === 0) {
      toast.error('Las fotos aún se están subiendo. Espera un momento.');
      return;
    }

    // Prepare payload
    const payload: CreateItemRequest = {
      item: cleanedValues,
      photos: photoSignedIds,
    };

    createMutation.mutate(payload);
  };

  const isSubmitting = createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[24px] font-light text-[#1e1e1e] uppercase tracking-[1.5px]">
            Onboardear Item
          </DialogTitle>
          <DialogDescription className="text-[14px] font-light text-[#9d9c9c]">
            Añade fotos y detalles del plato para que aparezca en los vídeos de los clientes
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Name (pre-filled, editable) */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre del Plato *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Ej: Pulpo a la gallega"
                      className="border-[#1e1e1e] focus-visible:ring-[#1e1e1e] font-light"
                    />
                  </FormControl>
                  <FormDescription className="text-[12px]">
                    Puedes editar el nombre que viene del TPV
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Photos - REQUIRED */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Fotos del Plato <span className="text-red-600">*</span>
              </label>
              <ImageUpload
                images={images}
                onImagesChange={handleImagesChange}
                maxImages={5}
                disabled={isSubmitting}
              />
              <p className="text-[12px] text-[#9d9c9c]">
                Sube al menos una foto de alta calidad del plato para generar los vídeos
              </p>
            </div>

            {/* Category */}
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría (opcional)</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      className="w-full px-3 py-2 text-sm border border-[#1e1e1e] rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e1e1e] font-light"
                    >
                      <option value="">Seleccionar categoría...</option>
                      {ITEM_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción (opcional)</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder="Descripción del plato para el menú..."
                      className="w-full min-h-[80px] px-3 py-2 text-sm border border-[#1e1e1e] rounded-md focus:outline-none focus:ring-2 focus:ring-[#1e1e1e] font-light"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!isDemoMode && itemData?.price_cents && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#9d9c9c]">Precio (TPV)</label>
                <div className="px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 font-light">
                  {formatPrice(itemData.price_cents, itemData.currency)}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="border-[#1e1e1e] text-[#1e1e1e] hover:bg-[#eeeeee] font-light"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || images.length === 0}
                className="bg-[#1e1e1e] text-white hover:bg-[#333333] font-light"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Onboardear Item
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
